import URLS from '@/store/urls';
import apiCall from '@/store/apiCall';
import notificationWithIcon from '@/utils/notification';
import { showErrorNotification } from '@/utils/errorHandler';

const actions = {
  // ─── Load blocks for a page ───────────────────────────
  async loadBlocks({ commit }, pageId) {
    commit('SET_BLOCK_TREE_LOADING');
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.companyPageBlocks}/page/${pageId}`,
        isToken: true,
      });
      const blocks = data.data || data;
      commit('SET_BLOCK_TREE', blocks);
      commit('PUSH_HISTORY', 'Load page');
      return blocks;
    } catch (err) {
      commit('SET_BLOCK_TREE_ERROR');
      showErrorNotification(err);
      throw err;
    }
  },

  // ─── Create a single block ────────────────────────────
  async createBlock({ commit, state }, blockData) {
    try {
      commit('PUSH_HISTORY', 'Add block');
      const { data } = await apiCall({
        type: 'POST',
        url: URLS.companyPageBlocks,
        body: {
          pageId: state.currentPageId,
          ...blockData,
        },
        isToken: true,
      });
      const block = data.data || data;
      commit('ADD_BLOCK', { block });
      commit('SET_SELECTED_BLOCK', block.id);
      return block;
    } catch (err) {
      showErrorNotification(err);
      throw err;
    }
  },

  // ─── Update a block ───────────────────────────────────
  async updateBlock({ commit }, { blockId, changes }) {
    try {
      commit('PUSH_HISTORY', 'Update block');
      const { data } = await apiCall({
        type: 'PUT',
        url: `${URLS.companyPageBlocks}/${blockId}`,
        body: changes,
        isToken: true,
      });
      const updated = data.data || data;
      commit('UPDATE_BLOCK', { blockId, changes: updated });
      return updated;
    } catch (err) {
      showErrorNotification(err);
      throw err;
    }
  },

  // ─── Update block locally (no API, for live editing) ──
  updateBlockLocal({ commit }, { blockId, changes }) {
    commit('PUSH_HISTORY', 'Edit block');
    commit('UPDATE_BLOCK', { blockId, changes });
  },

  // ─── Delete a block ───────────────────────────────────
  async deleteBlock({ commit }, blockId) {
    try {
      commit('PUSH_HISTORY', 'Delete block');
      await apiCall({
        type: 'DELETE',
        url: `${URLS.companyPageBlocks}/${blockId}`,
        isToken: true,
      });
      commit('REMOVE_BLOCK', blockId);
      notificationWithIcon('success', 'Block deleted');
      return true;
    } catch (err) {
      showErrorNotification(err);
      throw err;
    }
  },

  // ─── Reorder blocks (drag & drop) ────────────────────
  async reorderBlocks({ commit }, blocks) {
    try {
      commit('PUSH_HISTORY', 'Reorder blocks');
      commit('REORDER_BLOCKS', blocks);
      await apiCall({
        type: 'POST',
        url: `${URLS.companyPageBlocks}/reorder`,
        body: { blocks },
        isToken: true,
      });
      return true;
    } catch (err) {
      showErrorNotification(err);
      throw err;
    }
  },

  // ─── Bulk save (creates version snapshot) ─────────────
  async bulkSave({ commit, state }) {
    if (!state.currentPageId) return undefined;
    commit('SET_SAVING', true);
    try {
      const blocksPayload = state.blockTree.map((b) => ({
        id: b.id || undefined,
        parentId: b.parentId || null,
        blockType: b.blockType,
        sortOrder: b.sortOrder,
        content: b.content || {},
        styles: b.styles || {},
        isVisible: b.isVisible !== false,
        visibleOn: b.visibleOn || 'all',
        animation: b.animation || {},
      }));

      const { data } = await apiCall({
        type: 'POST',
        url: `${URLS.companyPageBlocks}/page/${state.currentPageId}/bulk-save`,
        body: { blocks: blocksPayload },
        isToken: true,
      });

      const blocks = data.data || data;
      commit('SET_BLOCK_TREE', blocks);
      commit('SET_DIRTY', false);
      notificationWithIcon('success', 'Page saved');
      return blocks;
    } catch (err) {
      showErrorNotification(err);
      throw err;
    } finally {
      commit('SET_SAVING', false);
    }
  },

  // ─── Versions ─────────────────────────────────────────
  async loadVersions({ commit, state }) {
    if (!state.currentPageId) return undefined;
    commit('SET_VERSIONS_LOADING');
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.companyPageBlocks}/page/${state.currentPageId}/versions`,
        isToken: true,
      });
      const versions = data.data || data;
      commit('SET_VERSIONS', versions);
      return versions;
    } catch (err) {
      showErrorNotification(err);
      throw err;
    }
  },

  async restoreVersion({ commit, state }, versionId) {
    if (!state.currentPageId) return undefined;
    try {
      commit('PUSH_HISTORY', 'Restore version');
      const { data } = await apiCall({
        type: 'POST',
        url: `${URLS.companyPageBlocks}/page/${state.currentPageId}/versions/${versionId}/restore`,
        isToken: true,
      });
      const blocks = data.data || data;
      commit('SET_BLOCK_TREE', blocks);
      commit('SET_DIRTY', true);
      notificationWithIcon('success', 'Version restored');
      return blocks;
    } catch (err) {
      showErrorNotification(err);
      throw err;
    }
  },

  // ─── Templates ────────────────────────────────────────
  async loadTemplates({ commit }, category) {
    commit('SET_TEMPLATES_LOADING');
    try {
      const params = category ? { category } : {};
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.companyPageBlocks}/templates`,
        params,
        isToken: true,
      });
      const templates = data.data || data;
      commit('SET_TEMPLATES', templates);
      return templates;
    } catch (err) {
      showErrorNotification(err);
      throw err;
    }
  },

  async applyTemplate({ commit, state }, { templateId, parentId }) {
    if (!state.currentPageId) return undefined;
    try {
      commit('PUSH_HISTORY', 'Apply template');
      const { data } = await apiCall({
        type: 'POST',
        url: `${URLS.companyPageBlocks}/templates/${templateId}/apply`,
        body: {
          pageId: state.currentPageId,
          parentId: parentId || null,
        },
        isToken: true,
      });
      const blocks = data.data || data;
      // Reload full block tree after template applied
      commit('SET_BLOCK_TREE', blocks);
      commit('SET_DIRTY', true);
      notificationWithIcon('success', 'Template applied');
      return blocks;
    } catch (err) {
      showErrorNotification(err);
      throw err;
    }
  },

  // ─── Create section scaffold (section + layer + row + 2 columns) ──
  async createSectionScaffold({ commit, state }, sectionContent = {}) {
    if (!state.currentPageId) return undefined;
    try {
      commit('PUSH_HISTORY', 'Add section scaffold');
      const { data } = await apiCall({
        type: 'POST',
        url: `${URLS.companyPageBlocks}/create-section-scaffold`,
        body: {
          pageId: state.currentPageId,
          sectionContent,
        },
        isToken: true,
      });
      const blocks = data.data || data;
      // Add all scaffold blocks to the tree
      blocks.forEach((block) => commit('ADD_BLOCK', { block }));
      // Select the section (first block in the scaffold)
      if (blocks.length > 0) {
        commit('SET_SELECTED_BLOCK', blocks[0].id);
      }
      return blocks;
    } catch (err) {
      showErrorNotification(err);
      throw err;
    }
  },

  // ─── Apply row preset (create/update columns for a row) ──
  async applyRowPreset({ commit, state, dispatch }, { rowId, spans }) {
    // Find existing columns for this row
    const existingColumns = state.blockTree
      .filter((b) => b.parentId === rowId && b.blockType === 'column')
      .sort((a, bk) => (a.sortOrder || 0) - (bk.sortOrder || 0));

    commit('PUSH_HISTORY', 'Apply row preset');

    // Delete excess columns
    const deletePromises = existingColumns
      .slice(spans.length)
      .map((col) => dispatch('deleteBlock', col.id));
    await Promise.all(deletePromises);

    // Update or create columns to match preset
    for (let i = 0; i < spans.length; i += 1) {
      if (i < existingColumns.length) {
        // Update existing column span
        commit('UPDATE_BLOCK', {
          blockId: existingColumns[i].id,
          changes: {
            content: {
              ...existingColumns[i].content,
              spanDesktop: spans[i],
              spanMobile: 12,
            },
            sortOrder: i,
          },
        });
      } else {
        // Create new column
        // eslint-disable-next-line no-await-in-loop
        await dispatch('createBlock', {
          parentId: rowId,
          blockType: 'column',
          sortOrder: i,
          content: {
            spanDesktop: spans[i],
            spanTablet: spans[i],
            spanMobile: 12,
          },
          styles: {},
        });
      }
    }
  },

  // ─── Subscription limits ──────────────────────────────
  async loadSubscriptionLimits({ commit }, companyId) {
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.companySubscriptions}/company/${companyId}/limits`,
        isToken: true,
      });
      const limits = data.data || data;
      commit('SET_SUBSCRIPTION_LIMITS', limits);
      return limits;
    } catch (err) {
      // Fall back to free tier defaults on error
      commit('SET_SUBSCRIPTION_LIMITS', {
        planType: 'free',
        maxPages: 5,
        maxBlocksPerPage: 20,
        customCssEnabled: false,
        customFontsEnabled: false,
      });
      return null;
    }
  },

  // ─── Clipboard ────────────────────────────────────────
  copyBlock({ commit, state }, blockId) {
    const block = state.blockTree.find((b) => b.id === blockId);
    if (block) {
      commit('SET_CLIPBOARD', block);
      notificationWithIcon('success', 'Block copied');
    }
  },

  async pasteBlock({ state, dispatch }) {
    if (!state.clipboard) return undefined;
    const { clipboard, blockTree, currentPageId } = state;
    const newBlock = {
      ...clipboard,
      id: undefined,
      pageId: currentPageId,
      sortOrder: blockTree.length,
    };
    delete newBlock.id;
    delete newBlock.addedAt;
    delete newBlock.updatedAt;
    return dispatch('createBlock', newBlock);
  },

  // ─── Undo / Redo ─────────────────────────────────────
  undo({ commit, getters }) {
    if (getters.canUndo) {
      commit('UNDO');
    }
  },

  redo({ commit, getters }) {
    if (getters.canRedo) {
      commit('REDO');
    }
  },

  // ─── Initialize builder ───────────────────────────────
  async initBuilder({ commit, dispatch }, { pageId, companyId }) {
    commit('RESET_BUILDER');
    commit('SET_PAGE_CONTEXT', { pageId, companyId });
    await Promise.all([
      dispatch('loadBlocks', pageId),
      dispatch('loadSubscriptionLimits', companyId),
      dispatch('loadTemplates'),
    ]);
  },

  // ─── Reset builder ───────────────────────────────────
  resetBuilder({ commit }) {
    commit('RESET_BUILDER');
  },
};

export default actions;
